import { useEffect, useMemo, useState, useCallback } from "react";
import { DatePicker, Tag, Tooltip, Typography, Space } from "antd";
import { MdPayments } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";

import ViewBtn from "@/components/Buttons/ViewBtn";
import TableComponent from "@/components/CommonUi/TableComponent";
import Card from "@/UI/Card";
import Modal from "@/UI/Modal";

import AddPayrollPayment from "./AddPayrollPayment";
import { loadAllStaff } from "@/redux/rtk/features/hrm/user/userSlice";
import { useGetPayslipForPaymentMonthWiseQuery } from "@/redux/rtk/features/hrm/payroll/payrollApi";

const { Title, Text } = Typography;

const fmtMoney = (v) => {
  const n = Number(v ?? 0);
  if (Number.isNaN(n)) return "0";
  // tweak to your locale/currency as needed
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(n);
};

const statusColor = (s) =>
  s === "PAID" ? "green" : s === "DUE" ? "orange" : "red";

export default function PayslipList() {
  const dispatch = useDispatch();

  const [pageConfig, setPageConfig] = useState({
    page: 1,
    count: 10,
    value: "monthWise",
    salaryMonth: undefined,
    salaryYear: undefined,
  });

  const { list: staffList } = useSelector((s) => s.users);

  // RTK Query fetch (auto refetches when pageConfig changes)
  const { data: payroll, isLoading } = useGetPayslipForPaymentMonthWiseQuery(pageConfig);

  // Modal state (object when open, null/false when closed)
  const [selectedPayslip, setSelectedPayslip] = useState(null);

  useEffect(() => {
    dispatch(loadAllStaff({ page: 1, count: 10, status: "true" }));
  }, [dispatch]);

  const onMonthChange = useCallback((date) => {
    setPageConfig((prev) => ({
      ...prev,
      value: "monthWise",
      salaryMonth: date ? dayjs(date).format("M") : undefined,
      page: 1,
    }));
  }, []);

  const onYearChange = useCallback((date) => {
    setPageConfig((prev) => ({
      ...prev,
      value: "monthWise",
      salaryYear: date ? dayjs(date).format("YYYY") : undefined,
      page: 1,
    }));
  }, []);

  const columns = useMemo(
    () => [
      { title: "ID", dataIndex: "id", key: "id" },

      {
        title: "Name",
        key: "name",
        dataIndex: "user",
        render: (user) => `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim(),
        renderCsv: (user) => `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim(),
      },

      {
        title: "Salary",
        dataIndex: "salary",
        key: "salary",
        render: (v) => fmtMoney(v),
        renderCsv: (v) => fmtMoney(v),
      },
      {
        title: "Salary Payable",
        dataIndex: "salaryPayable",
        key: "salaryPayable",
        render: (v) => fmtMoney(v),
        renderCsv: (v) => fmtMoney(v),
      },

      {
        title: "Month",
        key: "month",
        render: ({ salaryMonth }) => `${salaryMonth ?? ""}`,
        renderCsv: ({ salaryMonth }) => `${salaryMonth ?? ""}`,
      },
      {
        title: "Year",
        key: "year",
        render: ({ salaryYear }) => `${salaryYear ?? ""}`,
        renderCsv: ({ salaryYear }) => `${salaryYear ?? ""}`,
      },

      { title: "Bonus", dataIndex: "bonus", key: "bonus", render: fmtMoney, renderCsv: fmtMoney },
      { title: "Bonus Comment", dataIndex: "bonusComment", key: "bonusComment" },

      { title: "Deduction", dataIndex: "deduction", key: "deduction", render: fmtMoney, renderCsv: fmtMoney },
      { title: "Deduction Comment", dataIndex: "deductionComment", key: "deductionComment" },

      { title: "Total", dataIndex: "totalPayable", key: "totalPayable", render: fmtMoney, renderCsv: fmtMoney },
      { title: "Total Paid Amount", dataIndex: "totalPaidAmount", key: "totalPaidAmount", render: fmtMoney, renderCsv: fmtMoney },
      { title: "Total Due Amount", dataIndex: "totalDueAmount", key: "totalDueAmount", render: fmtMoney, renderCsv: fmtMoney },

      {
        title: "W Hours",
        dataIndex: "workingHour",
        key: "workingHour",
        render: (h) => `${Number(h ?? 0).toFixed(2)} hours`,
        renderCsv: (h) => `${Number(h ?? 0).toFixed(2)} hours`,
      },

      {
        title: "Status",
        dataIndex: "paymentStatus",
        key: "paymentStatus",
        render: (paymentStatus) => (
          <Tooltip title={paymentStatus === "PAID" ? "All payments cleared" : paymentStatus === "DUE" ? "Still has dues" : "Unpaid"}>
            <Tag color={statusColor(paymentStatus)}>{paymentStatus}</Tag>
          </Tooltip>
        ),
      },

      {
        id: 4,
        title: "",
        key: "action",
        csvOff: true,
        render: (payment) => [
          {
            key: "view",
            label: <ViewBtn title="View" path={`/admin/payslip/${payment?.id}`} />,
          },
          {
            key: "pay",
            label: (
              <button
                onClick={() => setSelectedPayslip(payment)}
                className="flex items-center gap-2 rounded disabled:cursor-not-allowed disabled:opacity-50 px-2 py-1"
                disabled={payment?.paymentStatus === "PAID"}
              >
                <MdPayments className="text-[1rem]" />
                Make a payment
              </button>
            ),
          },
        ],
      },
    ],
    []
  );

  const filters = useMemo(
    () => [
      {
        key: "paymentStatus",
        label: "Status",
        type: "select",
        options: [
          { label: "Paid", value: "PAID" },
          { label: "Unpaid", value: "UNPAID" },
          { label: "Due", value: "DUE" },
        ],
        className: "min-w-[100px] max-w-[160px]",
        popupClassName: "w-[220px]",
      },
      {
        key: "userId",
        label: "Name",
        type: "select",
        options:
          staffList?.map((u) => ({
            label: `${u?.firstName ?? ""} ${u?.lastName ?? ""}`.trim(),
            value: u?.id,
          })) ?? [],
        className: "min-w-[140px] max-w-[220px]",
        popupClassName: "w-[260px]",
      },
    ],
    [staffList]
  );

  return (
    <>
      <Card>
        <div className="mb-4 p-3">
          <Title level={3} style={{ marginBottom: 4 }}>
            PaySlips List
          </Title>
          <Text type="secondary">
            Filter by month and year to view payslips, make payments, or drill into details.
          </Text>
        </div>

        <div className="flex flex-wrap items-center gap-3 p-3">
          <Space size="middle" wrap>
            <div className="flex flex-nowrap items-center gap-2">
              <span className="text-base text-color-2 whitespace-nowrap">
                Select Month:
              </span>
              <DatePicker
                picker="month"
                format="MMM"
                style={{ maxWidth: 180 }}
                onChange={onMonthChange}
              />
            </div>

            <div className="flex flex-nowrap items-center gap-2">
              <span className="text-base text-color-2 whitespace-nowrap">
                Select Year:
              </span>
              <DatePicker
                picker="year"
                format="YYYY"
                style={{ maxWidth: 180 }}
                onChange={onYearChange}
              />
            </div>
          </Space>
        </div>


        <TableComponent
          title="Payslips"
          columns={columns}
          list={payroll?.getAllPayslip ?? []}
          total={payroll?.totalPayslip ?? 0}
          loading={isLoading}
          filters={filters}
          setPageConfig={setPageConfig}
        />
      </Card>

      <Modal
        outsideClick
        open={!!selectedPayslip}
        title="Make Payment"
        onClose={() => setSelectedPayslip(null)}
      >
        {selectedPayslip ? <AddPayrollPayment data={selectedPayslip} /> : null}
      </Modal>
    </>
  );
}
